import React, { useCallback, useMemo } from "react";
import { TextQuote } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Localizer } from "@/main/react/Types";
import {
    buildPassageExcerpt,
    resolvePassageBlockForNode,
    type PassageBlockMap,
} from "@/main/react/discussion/passage/mapBlocksToDom";

export type PassageCommentAffordanceProps = {
    localizer: Localizer;
    /** Null when the post is ineligible or the block join was refused: the affordance renders nothing. */
    blockMap: PassageBlockMap | null;
    /** The popover's current selection text, from `useHighlightPopover().currentSelection`. */
    selectionText: string;
    onCommentOnPassage: (blockId: string, excerpt: string) => void;
    /** Called after the tap so the host can close the selection popover and clear the selection. */
    onAfterTap?: () => void;
};

/**
 * Where a passage comment starts (design §3, §4.1): the member selects text inside the post and the
 * selection popover offers "Comment on this passage" beside the plain quote-and-reply it already offers.
 * The affordance appears ONLY when the selection sits inside one mapped block: a selection across the
 * title, the byline or two blocks gets the ordinary quote button and nothing else, because the server
 * verifies the excerpt against ONE block's text and would refuse anything else.
 *
 * <p>Anonymous readers see it too (A1): the sign-in ask is the composer's own, at Publish, and the words
 * they type survive it through the draft store. The selection is read from the live `Selection` at
 * render time rather than from the popover's text alone, because only the range knows which block it is
 * in.
 */
export function PassageCommentAffordance({
    localizer,
    blockMap,
    selectionText,
    onCommentOnPassage,
    onAfterTap,
}: PassageCommentAffordanceProps) {
    const resolved = useMemo(() => {
        if (!blockMap || blockMap.size === 0) {
            return null;
        }
        const excerpt = buildPassageExcerpt(selectionText);
        if (!excerpt) {
            return null;
        }
        const selection = typeof window !== "undefined" ? window.getSelection() : null;
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
            return null;
        }
        const range = selection.getRangeAt(0);
        const start = resolvePassageBlockForNode(blockMap, range.startContainer);
        const end = resolvePassageBlockForNode(blockMap, range.endContainer);
        if (!start || !end || start.blockId !== end.blockId) {
            return null;
        }
        return { blockId: start.blockId, excerpt };
    }, [blockMap, selectionText]);

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!resolved) {
            return;
        }
        onCommentOnPassage(resolved.blockId, resolved.excerpt);
        onAfterTap?.();
    }, [onAfterTap, onCommentOnPassage, resolved]);

    if (!resolved) {
        return null;
    }

    return (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-full border border-border bg-popover text-xs text-text-main hover:bg-muted"
            data-playwright-id="passage-comment-cta"
            data-block-id={resolved.blockId}
            onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onClick={handleClick}
        >
            <TextQuote className="size-3.5" aria-hidden={true} />
            {localizer("passage.comment.cta")}
        </Button>
    );
}

export default PassageCommentAffordance;
